import { Box, Grid, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FleetContext } from "../../shared/components/page_base";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LabelList,
} from "recharts";

function SoloDayGraphList(props) {
  const { data, displayedRobots, robotSummaryData } = props;
  let { fleetId } = useParams();
  const { robotStates } = useContext(FleetContext);
  const [hoursTicks, setHoursTicks] = useState([]);
  const [graphHeight, setGraphHeight] = useState(500);

  /// Run time graph data
  const [runTimeStatData, setRunTimeStatData] = useState([]);
  const [robotsRunTimeTotal, setRobotsRunTimeTotal] = useState(0);
  const [averageRunTimeHours, setAverageRunTimeHours] = useState(0);

  /// Idle time graph data
  const [idleTimeStatData, setIdleTimeStatData] = useState([]);
  const [robotsIdleTimeTotal, setRobotsIdleTimeTotal] = useState(0);
  const [averageIdleTimeHours, setAverageIdleTimeHours] = useState(0);

  /// Incidents graph data
  const [incidentStatData, setIncidentStatData] = useState([]);
  const [robotsIncidentTotal, setRobotsIncidentTotal] = useState(0);

  /// Tasks performed graph data (done + cancelled)
  const [taskStatData, setTaskStatData] = useState([]);
  const [robotsTaskTotal, setRobotsTaskTotal] = useState(0);

  /// Distance travelled graph data
  const [distanceStatData, setDistanceStatData] = useState([]);
  const [robotsDistanceTotal, setRobotsDistanceTotal] = useState(0);
  const [averageDistance, setAverageDistance] = useState(0);

  /// Area cleaned graph data
  const [areaCleanedStatData, setAreaCleanedStatData] = useState([]);
  const [robotsAreaCleanedTotal, setRobotsAreaCleanedTotal] = useState(0);
  const [averageAreaCleaned, setAverageAreaCleaned] = useState(0);

  useEffect(() => {
    if (
      !data ||
      !robotStates ||
      robotStates.size === 0 ||
      displayedRobots.size === 0
    ) {
      return;
    }

    let maxHours = 0;
    let count = 0;

    let tmpRunTimeStatData = [];
    let tmpTotalRunTime = 0;

    let tmpIdleTimeStatData = [];
    let tmpTotalIdleTime = 0;

    let tmpIncidentStatData = [];
    let tmpTotalIncident = 0;

    let tmpTaskStatData = [];
    let tmpTotalTask = 0;

    let tmpDistanceStatData = [];
    let tmpTotalDistance = 0;

    let tmpAreaCleanedStatData = [];
    let tmpTotalAreaCleaned = 0;

    let robotSet = new Set();

    setGraphHeight(
      Math.max(500, 200 + Object.keys(robotSummaryData).length * 100)
    );

    for (const [robot_id, robot_summary] of Object.entries(robotSummaryData)) {
      /// Ignore data we don't wanna show
      if (!displayedRobots.get(robot_id).show) {
        continue;
      }

      robotSet.add(robot_id);

      /// Get stat data on run time
      const runStr = "" + robot_summary["total_run_time"];
      const runHours = runStr.split(":")[0];
      const runMinutes = runStr.split(":")[1];
      const runSeconds = runStr.split(":")[2];
      const runValue =
        parseInt(runHours) * 3600 +
        parseInt(runMinutes) * 60 +
        parseInt(runSeconds);
      tmpRunTimeStatData.push({
        name: robotStates.get(robot_id).name,
        fill: displayedRobots.get(robot_id).color,
        "Run time": runValue,
      });
      tmpTotalRunTime += runValue;
      maxHours = Math.max(maxHours, Math.ceil(runValue / 3600));

      /// Get stat data on idle time
      const idleStr = "" + robot_summary["total_idle_duration"];
      const idleHours = idleStr.split(":")[0];
      const idleMinutes = idleStr.split(":")[1];
      const idleSeconds = idleStr.split(":")[2];
      const idleValue =
        parseInt(idleHours) * 3600 +
        parseInt(idleMinutes) * 60 +
        parseInt(idleSeconds);
      tmpIdleTimeStatData.push({
        name: robotStates.get(robot_id).name,
        fill: displayedRobots.get(robot_id).color,
        "Idle time": idleValue,
      });
      tmpTotalIdleTime += idleValue;
      maxHours = Math.max(maxHours, Math.ceil(idleValue / 3600));

      /// Setup the incident stat data
      tmpIncidentStatData.push({
        robot_id: robot_id,
        name: robotStates.get(robot_id).name,
        fill: displayedRobots.get(robot_id).color,
        "Very low severity": 0,
        "Low severity": 0,
        "Medium severity": 0,
        "High severity": 0,
      });

      /// Setup the task stat data
      tmpTaskStatData.push({
        robot_id: robot_id,
        name: robotStates.get(robot_id).name,
        fill: displayedRobots.get(robot_id).color,
        "Task done": 0,
        "Task cancelled": 0,
      });

      /// Setup the distance stat data
      tmpDistanceStatData.push({
        robot_id: robot_id,
        name: robotStates.get(robot_id).name,
        fill: displayedRobots.get(robot_id).color,
        Distance: 0,
      });

      /// Setup the area cleaned stat data
      if (
        robotStates.get(robot_id).type === "Vacuum" ||
        robotStates.get(robot_id).type === "Scrubber"
      ) {
        tmpAreaCleanedStatData.push({
          robot_id: robot_id,
          name: robotStates.get(robot_id).name,
          fill: displayedRobots.get(robot_id).color,
          "Area cleaned": 0,
        });
      }

      count++;
    }

    let tmpHoursTicks = [];
    for (let index = 1; index <= maxHours; index++) {
      tmpHoursTicks.push(index * 3600);
    }
    setHoursTicks(tmpHoursTicks);

    setRobotsRunTimeTotal(tmpTotalRunTime);
    setAverageRunTimeHours(tmpTotalRunTime / count);
    setRunTimeStatData(tmpRunTimeStatData);

    setRobotsIdleTimeTotal(tmpTotalIdleTime);
    setAverageIdleTimeHours(tmpTotalIdleTime / count);
    setIdleTimeStatData(tmpIdleTimeStatData);

    /// Get stat data on incidents
    if ("incidents" in data) {
      data["incidents"].forEach((incident) => {
        /// Only add data for robots we wanna show
        if (displayedRobots.get(incident["robot_id"]).show) {
          for (let i = 0; i < tmpIncidentStatData.length; i++) {
            /// Get the proper robot data to update
            if (tmpIncidentStatData[i]["robot_id"] === incident["robot_id"]) {
              /// Update fields based on incident severity
              switch (incident["severity"]) {
                case 0:
                  tmpIncidentStatData[i]["Very low severity"] += 1;
                  break;
                case 1:
                  tmpIncidentStatData[i]["Low severity"] += 1;
                  break;
                case 2:
                  tmpIncidentStatData[i]["Medium severity"] += 1;
                  break;
                case 3:
                  tmpIncidentStatData[i]["High severity"] += 1;
                  break;
                default:
                  break;
              }
              tmpTotalIncident++;
            }
          }
        }
      });
    }

    setRobotsIncidentTotal(tmpTotalIncident);
    setIncidentStatData(tmpIncidentStatData);

    count = 0;
    /// Get stat data on tasks
    if ("tasks" in data) {
      data["tasks"].forEach((task) => {
        /// Only add data for robots we wanna show
        if (displayedRobots.get(task["robot_id"]).show) {
          for (let i = 0; i < tmpTaskStatData.length; i++) {
            /// Get the proper robot data to update
            if (tmpTaskStatData[i]["robot_id"] === task["robot_id"]) {
              /// Update fields based on task status
              if (task["status"] === "done") {
                tmpTaskStatData[i]["Task done"] += 1;
              } else if (task["status"] === "cancelled") {
                tmpTaskStatData[i]["Task cancelled"] += 1;
              }
              tmpTotalTask++;
              /// Update distance & area cleaned
              for (let j = 0; j < task["sub_tasks"].length; j++) {
                tmpDistanceStatData[i]["Distance"] +=
                  task["sub_tasks"][j]["distance_travelled"];
                tmpTotalDistance += task["sub_tasks"][j]["distance_travelled"];
                for (let k = 0; k < tmpAreaCleanedStatData.length; k++) {
                  if (
                    tmpAreaCleanedStatData[k]["robot_id"] ===
                      task["robot_id"] &&
                    (robotStates.get(task["robot_id"]).type === "Vacuum" ||
                      robotStates.get(task["robot_id"]).type === "Scrubber")
                  ) {
                    tmpAreaCleanedStatData[k]["Area cleaned"] +=
                      task["sub_tasks"][j]["area_cleaned"];
                    tmpTotalAreaCleaned += task["sub_tasks"][j]["area_cleaned"];
                  }
                }
              }
              count++;
            }
          }
        }
      });
    }
    setRobotsTaskTotal(tmpTotalTask);
    setTaskStatData(tmpTaskStatData);

    setRobotsDistanceTotal(tmpTotalDistance);
    setAverageDistance(tmpTotalDistance / robotSet.size);
    setDistanceStatData(tmpDistanceStatData);

    setRobotsAreaCleanedTotal(tmpTotalAreaCleaned);
    setAverageAreaCleaned(tmpTotalAreaCleaned / robotSet.size);
    setAreaCleanedStatData(tmpAreaCleanedStatData);
  }, [data, displayedRobots, fleetId, robotStates, robotSummaryData]);

  const secToStr = (value) => {
    const seconds = Math.floor(value % 60);
    const minutes = Math.floor((value / 60) % 60);
    const hours = Math.floor(value / 3600);

    return (
      (hours < 10 ? "0" + hours : hours) +
      ":" +
      (minutes < 10 ? "0" + minutes : minutes) +
      ":" +
      (seconds < 10 ? "0" + seconds : seconds)
    );
  };

  if (!data || !robotStates || robotStates.size === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          marginLeft: "1.5rem",
          marginRight: "1.5rem",
          marginBottom: "1.5rem",
          flexGrow: 1,
        }}
      >
        <Typography
          color="#c9cdd3"
          variant="h5"
          sx={{
            marginTop: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          Summary Breakdown
        </Typography>
        <Grid container spacing={"1.5rem"}>
          {/** TOTAL RUN TIME GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Run Time -</b> {secToStr(robotsRunTimeTotal)}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={runTimeStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Hours",
                    }}
                    tickFormatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                    ticks={hoursTicks}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip
                    formatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                  />
                  <ReferenceLine
                    x={averageRunTimeHours}
                    stroke="grey"
                    label={{
                      position: "top",
                      value: "Average - " + secToStr(averageRunTimeHours),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  <Bar
                    layout="vertical"
                    dataKey="Run time"
                    barSize={50}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Run time"
                      position="right"
                      formatter={(value, name, props) => {
                        return secToStr(value);
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL IDLE TIME GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Idle Time -</b> {secToStr(robotsIdleTimeTotal)}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={idleTimeStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Hours",
                    }}
                    tickFormatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                    ticks={hoursTicks}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip
                    formatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                  />
                  <ReferenceLine
                    x={averageIdleTimeHours}
                    stroke="grey"
                    label={{
                      position: "top",
                      value: "Average - " + secToStr(averageIdleTimeHours),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  <Bar
                    layout="vertical"
                    dataKey="Idle time"
                    barSize={50}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Idle time"
                      position="right"
                      formatter={(value, name, props) => {
                        return secToStr(value);
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** INCIDENT GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Number of Incidents -</b> {robotsIncidentTotal}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={incidentStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Incidents",
                    }}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip />
                  <Bar
                    layout="vertical"
                    dataKey="Very low severity"
                    barSize={20}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Very low severity"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "Very low severity" : "";
                      }}
                    />
                    <LabelList dataKey="Very low severity" position="right" />
                  </Bar>
                  <Bar
                    layout="vertical"
                    dataKey="Low severity"
                    barSize={20}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Low severity"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "Low severity" : "";
                      }}
                    />
                    <LabelList dataKey="Low severity" position="right" />
                  </Bar>
                  <Bar
                    layout="vertical"
                    dataKey="Medium severity"
                    barSize={20}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Medium severity"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "Medium severity" : "";
                      }}
                    />
                    <LabelList dataKey="Medium severity" position="right" />
                  </Bar>
                  <Bar
                    layout="vertical"
                    dataKey="High severity"
                    barSize={20}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="High severity"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "High severity" : "";
                      }}
                    />
                    <LabelList dataKey="High severity" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TASK GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Number of Tasks Performed -</b> {robotsTaskTotal}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={taskStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Tasks",
                    }}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip />
                  <Bar
                    layout="vertical"
                    dataKey="Task done"
                    barSize={35}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Task done"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "Task done" : "";
                      }}
                    />
                    <LabelList dataKey="Task done" position="right" />
                  </Bar>
                  <Bar
                    layout="vertical"
                    dataKey="Task cancelled"
                    barSize={35}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Task cancelled"
                      position="insideLeft"
                      fill="white"
                      formatter={(value, name, props) => {
                        return value > 0 ? "Task cancelled" : "";
                      }}
                    />
                    <LabelList dataKey="Task cancelled" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** DISTANCE GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Distance Travelled -</b>{" "}
                {robotsDistanceTotal.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
                m
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={distanceStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Distance (in meters)",
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      });
                    }}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip
                    formatter={(value, name, props) => {
                      return (
                        value.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) + "m"
                      );
                    }}
                  />
                  <ReferenceLine
                    x={averageDistance}
                    stroke="grey"
                    label={{
                      position: "top",
                      value:
                        "Average - " +
                        averageDistance.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) +
                        "m",
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  <Bar
                    layout="vertical"
                    dataKey="Distance"
                    barSize={50}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Distance"
                      position="right"
                      formatter={(value, name, props) => {
                        return (
                          value.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          }) + "m"
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** AREA CLEANED GRAPH */}
          <Grid item xs={6}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Area Cleaned -</b>{" "}
                {robotsAreaCleanedTotal.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
                m<sup>2</sup>
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <BarChart
                  width={450}
                  height={300}
                  data={areaCleanedStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                  layout="vertical"
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      position: "bottom",
                      value: "Area cleaned (in square meters)",
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      });
                    }}
                  />
                  <YAxis width={70} dataKey="name" type="category" />
                  <Tooltip
                    formatter={(value, name, props) => {
                      return (
                        value.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) + "m²"
                      );
                    }}
                  />
                  <ReferenceLine
                    x={averageAreaCleaned}
                    stroke="grey"
                    label={{
                      position: "top",
                      value:
                        "Average - " +
                        averageAreaCleaned.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) +
                        "m²",
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  <Bar
                    layout="vertical"
                    dataKey="Area cleaned"
                    barSize={50}
                    activeBar={false}
                  >
                    <LabelList
                      dataKey="Area cleaned"
                      position="right"
                      formatter={(value, name, props) => {
                        return (
                          value.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          }) + "m²"
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
}

export default SoloDayGraphList;
